import { authService } from "../../services/auth.service";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { motion } from "framer-motion";
import {
  Mail,
  Lock,
  ArrowRight,
  Loader2,
  Zap,
  ChevronLeft,
  ShieldCheck,
  Video
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const getSafeReturnPath = (from: { pathname?: string; search?: string } | undefined): string => {
  const pathname = from?.pathname;
  if (!pathname || !pathname.startsWith("/") || pathname.startsWith("//")) {
    return "/dashboard";
  }
  return `${pathname}${from?.search || ""}`;
};

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getSafeReturnPath(
    (location.state as { from?: { pathname?: string; search?: string } } | null)?.from
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await authService.signin(formData);
      navigate(returnPath, { replace: true });
    } catch (err: any) {
      setError(err.message || "Invalid email or password. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center p-6 font-sans relative overflow-hidden">
      {/* Soft Background Accents */}
      <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-indigo-50 blur-[120px] rounded-full opacity-50"></div>
      <div className="absolute bottom-0 left-0 w-[50%] h-[50%] bg-purple-50 blur-[120px] rounded-full opacity-50"></div>

      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-12 items-center relative z-10">

        {/* Marketing Side */}
        <div className="hidden lg:flex flex-col justify-center space-y-8 pr-12">
          <div className="flex items-center gap-3">
            <img src="/vidlaya-logo.png" className="w-14 h-14 object-contain" alt="VidLaya" />
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">VidLaya</h1>
          </div>

          <div className="space-y-6">
            <h2 className="text-5xl font-extrabold leading-[1.1] tracking-tight text-slate-900">
              Welcome back <br />
              to the studio.
            </h2>
            <p className="text-slate-500 font-medium text-lg max-w-md leading-relaxed">
              Sign in to your account and continue creating professional videos with ease.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 pt-4">
            <div className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                <Video size={20} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">Professional Studio</p>
                <p className="text-xs text-slate-500 font-medium">All your projects in one clean workspace.</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-slate-100 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                <ShieldCheck size={20} />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">Safe & Secure</p>
                <p className="text-xs text-slate-500 font-medium">Your data and content are always protected.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md mx-auto"
        >
          <div className="bg-white border border-slate-200 rounded-[32px] p-10 lg:p-12 shadow-xl shadow-slate-200/50">
            <div className="mb-8 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
                <h3 className="text-2xl font-bold text-slate-900">Sign In</h3>
              </div>
              <p className="text-slate-500 font-medium text-sm">Enter your details to access your dashboard.</p>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-rose-50 border border-rose-100 text-rose-600 text-xs font-semibold text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 ml-1 flex justify-start">Email Address</label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={18} />
                  <Input
                    type="email"
                    name="email"
                    placeholder="name@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-11 h-12 bg-slate-50 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <label className="text-xs font-bold text-slate-700">Password</label>
                  <a href="/forgot-password" className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors">Forgot?</a>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={18} />
                  <Input
                    type="password"
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-11 h-12 bg-slate-50 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-md shadow-indigo-100 active:scale-[0.98] mt-2"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">
                    Sign In
                    <ArrowRight size={18} />
                  </span>
                )}
              </Button>
            </form>

            <div className="my-8 flex items-center gap-4">
              <div className="h-[1px] flex-1 bg-slate-100"></div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">or</span>
              <div className="h-[1px] flex-1 bg-slate-100"></div>
            </div>

            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={async (credentialResponse) => {
                  if (credentialResponse.credential) {
                    setIsLoading(true);
                    try {
                      await authService.googleSignin(credentialResponse.credential);
                      navigate(returnPath, { replace: true });
                    } catch (err: any) {
                      setError(err.message || "Google sign-in failed. Please try again.");
                      setIsLoading(false);
                    }
                  }
                }}
                onError={() => setError("Google sign-in failed. Please try again.")}
                shape="pill"
                theme="outline"
                text="signin_with"
                width="100%"
              />
            </div>

            <div className="mt-10 text-center">
              <p className="text-slate-500 text-sm font-medium">
                Don't have an account?{" "}
                <a
                  href="/signup"
                  className="text-indigo-600 font-bold hover:text-indigo-800 hover:underline transition-all ml-1"
                >
                  Sign up for free
                </a>
              </p>
            </div>
          </div>

          {/* Bottom Link */}
          <div className="mt-8 flex justify-center">
            <a
              href="https://vidlaya.com"
              className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors group text-sm font-medium"
            >
              <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              Back to VidLaya.com
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
